import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Modal from 'src/components/layouts/Modal/Modal';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Select, { Option } from 'src/components/ui/Select/Select';
import Input from 'src/components/ui/Input/Input';
import Icon from 'src/components/ui/Icon/Icon';
import Divider from 'src/components/ui/Divider/Divider';
import { DatePicker } from 'src/components/ui/DatePicker/DatePicker';
import { components } from 'src/generated/v1';
import { useWorkflowsConditionsAttributesQuery } from 'src/services/workflows';
import { useAllCountriesListQuery } from 'src/services/countries';
import { getWorkflows } from 'src/redux/workflows/selectors';
import { formatFilterKey, getOperatorTranslation } from 'src/utils/general';
import styles from './ConditionSelectionOverlay.module.scss';

// FIXME(be-merge): The backend feature branch adds `operators`, `field_type` and
// `options` to WorkflowConditionAttribute. Those fields aren't yet in the merged
// OpenAPI schema, so we extend the generated type locally to keep consumers
// compiling. Once the BE branch is merged and `yarn generate:api-code` re-runs,
// delete the intersection below — `components['schemas']['WorkflowConditionAttribute']`
// will already include these fields.
type ConditionAttribute = components['schemas']['WorkflowConditionAttribute'] & {
  operators?: string[];
  field_type?: string;
  options?: string[];
};

export type ConditionFormData = {
  attribute: string;
  attributeLabel: string;
  condition: string;
  conditionLabel: string;
  value: string;
  valueLabel: string;
  valueType: string;
};

export type ConditionGroupFormData = {
  operator: 'and' | 'or';
  conditions: ConditionFormData[];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConditionSelect: (groupData: ConditionGroupFormData) => void;
  initialData?: ConditionGroupFormData | null;
};

function ConditionRow({
  index,
  conditionData,
  attributes,
  countryOptions,
  model,
  onAttributeChange,
  onConditionChange,
  onValueChange,
  onDelete,
  showDelete,
}: {
  index: number;
  conditionData: ConditionFormData;
  attributes: ConditionAttribute[];
  countryOptions: Option[];
  model?: string;
  onAttributeChange: (index: number, attribute: ConditionAttribute | null) => void;
  onConditionChange: (index: number, option: Option) => void;
  onValueChange: (index: number, value: string, label: string) => void;
  onDelete: (index: number) => void;
  showDelete: boolean;
}) {
  const { t } = useTranslation();

  const attributeOptions: Option[] = attributes.map(attr => ({
    value: attr.name,
    label: t(`general.${attr.name}`, { defaultValue: attr.name }),
  }));

  const selectedAttribute = attributes.find(attr => attr.name === conditionData.attribute) || null;

  const fieldType = selectedAttribute?.field_type;
  const isCountry = fieldType === 'country';
  const isSelection = fieldType === 'selection';
  const isBoolean = fieldType === 'boolean';
  const isDatetime = fieldType === 'datetime';
  const isNumber = fieldType === 'integer' || fieldType === 'float';

  const operatorOptions: Option[] = (selectedAttribute?.operators || [])
    .filter(op => (isDatetime ? op === '>' || op === '<' : true))
    .map(op => ({
      value: op,
      label: getOperatorTranslation(op),
    }));
  const hasOptions = (selectedAttribute?.options?.length ?? 0) > 0;

  const booleanOptions: Option[] = [
    { value: 'True', label: t('general.true') },
    { value: 'False', label: t('general.false') },
  ];

  const selectionValueOptions: Option[] = isSelection
    ? hasOptions
      ? (selectedAttribute!.options || []).map(o => ({ value: o, label: formatFilterKey(o, model) }))
      : booleanOptions
    : [];

  const hasData = conditionData.attribute || conditionData.condition || conditionData.value;
  const valueDisabled = !conditionData.attribute || !conditionData.condition;

  return (
    <div className={styles.conditionRow}>
      <div className={styles.fieldColumn}>
        <span className={styles.fieldLabel}>{t('workflows.condition_overlay.field')}</span>
        <Select
          menuFixed
          options={attributeOptions}
          placeholder={t('workflows.condition_overlay.field_placeholder')}
          isSearchable
          onChange={option => {
            const attr = attributes.find(a => a.name === option?.value) || null;
            onAttributeChange(index, attr);
          }}
          defaultValue={
            conditionData.attribute
              ? {
                  value: conditionData.attribute,
                  label: t(`general.${conditionData.attribute}`, { defaultValue: conditionData.attribute }),
                }
              : undefined
          }
        />
      </div>
      <div className={styles.fieldColumn}>
        <Select
          menuFixed
          options={operatorOptions}
          disabled={!selectedAttribute}
          onChange={option => onConditionChange(index, option)}
          defaultValue={
            conditionData.condition
              ? // Translate the stored operator (e.g. "ilike" -> "enthält"); when
                // editing an existing workflow conditionLabel is empty, so without
                // this the raw operator value was shown.
                { value: conditionData.condition, label: getOperatorTranslation(conditionData.condition) }
              : undefined
          }
        />
      </div>
      <div className={styles.fieldColumn}>
        <span className={styles.fieldLabel}>{t('workflows.condition_overlay.value_label')}</span>
        {isCountry ? (
          <Select
            menuFixed
            options={countryOptions}
            placeholder={t('workflows.condition_overlay.value_placeholder')}
            isSearchable
            onChange={option => onValueChange(index, option?.value ?? '', option?.label ?? '')}
            defaultValue={
              conditionData.value
                ? { value: conditionData.value, label: conditionData.valueLabel || conditionData.value }
                : undefined
            }
            disabled={valueDisabled}
          />
        ) : isSelection || isBoolean ? (
          <Select
            menuFixed
            options={isBoolean ? booleanOptions : selectionValueOptions}
            placeholder={t('workflows.condition_overlay.value_placeholder')}
            isSearchable={!isBoolean}
            onChange={option => onValueChange(index, option?.value ?? '', option?.label ?? '')}
            defaultValue={
              conditionData.value
                ? { value: conditionData.value, label: conditionData.valueLabel || conditionData.value }
                : undefined
            }
            disabled={valueDisabled}
          />
        ) : isDatetime ? (
          <DatePicker
            disabled={valueDisabled}
            defaultValue={conditionData.value ? dayjs(conditionData.value) : null}
            onChange={v => onValueChange(index, v ?? '', v ?? '')}
          />
        ) : (
          <Input
            disabled={valueDisabled}
            placeholder={t('workflows.condition_overlay.value_placeholder')}
            value={conditionData.value}
            type={isNumber ? 'number' : 'text'}
            onChange={e => onValueChange(index, e.target.value, e.target.value)}
          />
        )}
      </div>
      {showDelete && hasData && (
        <button
          type="button"
          className={styles.deleteButton}
          onClick={() => onDelete(index)}
          aria-label="Delete condition">
          <Icon name="delete" width={18} height={18} className="status-error-full" />
        </button>
      )}
    </div>
  );
}

const emptyCondition: ConditionFormData = {
  attribute: '',
  attributeLabel: '',
  condition: '',
  conditionLabel: '',
  value: '',
  valueLabel: '',
  valueType: 'string',
};

export default function ConditionSelectionOverlay({ isOpen, onClose, onConditionSelect, initialData }: Props) {
  const { t } = useTranslation();

  const [operator, setOperator] = useState<'and' | 'or'>('and');
  const [conditions, setConditions] = useState<ConditionFormData[]>([{ ...emptyCondition }]);

  const workflowState = useSelector(getWorkflows);
  const { selectedTrigger } = workflowState;

  const { data: attributesData } = useWorkflowsConditionsAttributesQuery(
    selectedTrigger?.model || '',
    !!selectedTrigger?.model,
  );

  const attributes: ConditionAttribute[] = attributesData ?? [];

  const { data: countriesData } = useAllCountriesListQuery();
  const countryOptions: Option[] = (countriesData ?? [])
    .filter(c => !!c.name)
    .map(c => ({ value: c.name as string, label: c.name as string }));

  useEffect(() => {
    if (isOpen && initialData) {
      setOperator(initialData.operator);
      setConditions(
        initialData.conditions.length > 0 ? initialData.conditions.map(c => ({ ...c })) : [{ ...emptyCondition }],
      );
    }
  }, [isOpen]);

  const resetForm = () => {
    setOperator('and');
    setConditions([{ ...emptyCondition }]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAttributeChange = (index: number, attribute: ConditionAttribute | null) => {
    setConditions(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        attribute: attribute?.name ?? '',
        attributeLabel: attribute ? t(`general.${attribute.name}`, { defaultValue: attribute.name }) : '',
        condition: '',
        conditionLabel: '',
        value: '',
        valueLabel: '',
        valueType: attribute?.field_type ?? 'char',
      };
      return updated;
    });
  };

  const handleConditionChange = (index: number, option: Option) => {
    setConditions(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        condition: option.value,
        conditionLabel: option.label,
      };
      return updated;
    });
  };

  const handleValueChange = (index: number, value: string, label: string) => {
    setConditions(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        value,
        valueLabel: label,
      };
      return updated;
    });
  };

  const handleDeleteRow = (index: number) => {
    setConditions(prev => {
      if (prev.length <= 1) {
        return [{ ...emptyCondition }];
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const isConditionComplete = (c: ConditionFormData) =>
    !!c.attribute && !!c.condition && c.value !== '' && c.value !== null && c.value !== undefined;

  const handleAdd = () => {
    const validConditions = conditions.filter(isConditionComplete);
    if (validConditions.length === 0 || validConditions.length !== conditions.length) return;

    onConditionSelect({
      operator,
      conditions: validConditions,
    });
    resetForm();
    onClose();
  };

  const hasValidCondition = conditions.length > 0 && conditions.every(isConditionComplete);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} hasCloseButton>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className="typo-title-24">{t('workflows.condition_overlay.title')}</h2>
          <p className="typo-text-14 text-faded mt-4">{t('workflows.condition_overlay.subtitle')}</p>
        </div>

        <Divider customColor="border-subtle-transparent" className={styles.fullWidthDivider} />

        <div className={styles.body}>
          <p className={styles.whenLabel}>{t('workflows.condition_overlay.when')}</p>

          {conditions.map((conditionData, index) => (
            <React.Fragment key={index}>
              {index > 0 && (
                <div className={styles.operatorRow}>
                  <div className={styles.operatorToggle}>
                    <button
                      type="button"
                      className={`${styles.operatorButton} ${operator === 'and' ? styles.operatorButtonActive : ''}`}
                      onClick={() => setOperator('and')}>
                      {t('workflows.condition_overlay.and')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.operatorButton} ${operator === 'or' ? styles.operatorButtonActive : ''}`}
                      onClick={() => setOperator('or')}>
                      {t('workflows.condition_overlay.or')}
                    </button>
                  </div>
                </div>
              )}
              <ConditionRow
                index={index}
                conditionData={conditionData}
                attributes={attributes}
                countryOptions={countryOptions}
                model={selectedTrigger?.model}
                onAttributeChange={handleAttributeChange}
                onConditionChange={handleConditionChange}
                onValueChange={handleValueChange}
                onDelete={handleDeleteRow}
                showDelete={
                  conditions.length > 1 || !!(conditionData.attribute || conditionData.condition || conditionData.value)
                }
              />
            </React.Fragment>
          ))}
        </div>

        <div className={styles.footer}>
          <Button tone={ButtonToneProps.SECONDARY} title={t('general.cancel')} onClick={handleClose} />
          <Button
            tone={ButtonToneProps.PRIMARY_BLUE}
            title={t('workflows.condition_overlay.add')}
            onClick={handleAdd}
            disabled={!hasValidCondition}
          />
        </div>
      </div>
    </Modal>
  );
}
